import { useState, useEffect, useRef, Fragment } from "react"
import { Head } from "@inertiajs/react"
import { trackEvent } from "../../lib/analytics"
import QualForm from "./QualForm"
import HowItWorks from "./HowItWorks"
import Testimonials from "./Testimonials"
import FAQ from "./FAQ"
import SchemaWidget from "../../components/SchemaWidget"
import { renderCopy } from "../../components/renderCopy"
import ShareAction from "../../components/ShareAction"
import ScrollProgressBar from "../../components/ScrollProgressBar"
import { prefersReducedMotion } from "../../components/useScroll"

export default function Landing({
  target,
  legal_theory,
  document_title,
  case_file_count,
  turnstile_site_key,
  error,
  preview,
  site_name,
  firm_name,
  firm_tagline,
  header_tagline,
  home_link_target,
  contact_email,
  marketing_consent_copy,
  entry_surface,
  copy = {},
  firm_website_url,
  firm_website_display,
  comparison_disclaimer,
  share,
  cta_phone,
}) {
  // The footer chrome's {{token}} substitutions (act:336bc672). One map per
  // FirmDetail::FUNNEL_CHROME_TOKENS — every token in that list substitutes on
  // every funnel-chrome page, both directions, locked by
  // spec/frontend/funnel_chrome_copy_tokens_parity_spec.rb. Plain strings here
  // (no styled nodes): the landing footer's disclaimer names the firm, it does
  // not link it — the site link is its own <a> below.
  const footerCopyVars = {
    firm_name,
    site_name,
    contact_email,
    company_name: target?.company_name,
  }
  const [reduce] = useState(prefersReducedMotion)
  const lc = target.landing_content || {}
  const scrollTracked = useRef(false)
  const viewTracked = useRef(false)
  // When the qual form reaches the disqualified terminal state, hide the
  // "Sign Up With…" section heading so the invitation doesn't sit above the
  // "We're sorry" message.
  const [signupDisqualified, setSignupDisqualified] = useState(false)

  // biome-ignore lint/correctness/useExhaustiveDependencies: This hook does not specify its dependency on target.theory_slug. these effects are ANALYTICS instruments on the funnel. Adding a dependency makes an effect RE-RUN, so on a trackEvent effect that is a duplicate conversion event — silent corruption of the numbers ad spend is set from, which nothing here would redden (the Ruby suite parses this source, it does not render it). Deliberate per-site, not deferred: see act:5be85a36 for the per-site plan and the browser check that can falsify it.
  useEffect(() => {
    if (!viewTracked.current && !preview) {
      viewTracked.current = true
      // entry_surface (act:e3aab5d3) rides the funnel_view event ONLY when this
      // arrival actually came from an investigation article (?via=investigation
      // → the server-validated prop); a direct arrival omits it. This is the
      // article-referred arrival count InvestigationStatsQuery reads.
      trackEvent("funnel_view", {
        target_slug: target.slug,
        theory_slug: target.theory_slug,
        ...(entry_surface ? { entry_surface } : {}),
      })
    }
  }, [target.slug, preview, entry_surface])

  // biome-ignore lint/correctness/useExhaustiveDependencies: This hook does not specify its dependency on target.theory_slug. these effects are ANALYTICS instruments on the funnel. Adding a dependency makes an effect RE-RUN, so on a trackEvent effect that is a duplicate conversion event — silent corruption of the numbers ad spend is set from, which nothing here would redden (the Ruby suite parses this source, it does not render it). Deliberate per-site, not deferred: see act:5be85a36 for the per-site plan and the browser check that can falsify it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: This hook does not specify its dependency on preview. these effects are ANALYTICS instruments on the funnel. Adding a dependency makes an effect RE-RUN, so on a trackEvent effect that is a duplicate conversion event — silent corruption of the numbers ad spend is set from, which nothing here would redden (the Ruby suite parses this source, it does not render it). Deliberate per-site, not deferred: see act:5be85a36 for the per-site plan and the browser check that can falsify it.
  useEffect(() => {
    const el = document.getElementById("signup")
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !scrollTracked.current && !preview) {
          scrollTracked.current = true
          trackEvent("scroll_to_form", {
            target_slug: target.slug,
            theory_slug: target.theory_slug,
          })
        }
      },
      { threshold: 0.3 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [target.slug])

  // The phone share chip's visibility (act:de0f0ed2 — the operator's design).
  // A small sticky control in the LOWER right: the one corner of this page not
  // already spoken for. The nav's upper right is the primary CTA, measured to
  // 28px of slack at 390px with the brand already truncating at 320px, and a
  // taller nav would also push the #signup heading under the bar on exactly the
  // arrival a shared link creates (scroll-mt-24 leaves 19px). Both of those are
  // costs of TOP chrome; the bottom-right corner pays neither.
  //
  // Two rules, and both are load-bearing rather than polish:
  //   1. ABSENT until the visitor has reached the form. This page is a paid ad
  //      destination whose measured job is a qualified sign-up, so nothing new
  //      competes on the way down — the same principle as the case file
  //      suppressing its share once an interview is live.
  //   2. HIDDEN again whenever the form is on screen. Probed live at 390x844:
  //      a chip at a 16px bottom-right inset lands on top of the qualifying
  //      question's "No" button. Hiding it while the form is visible is what
  //      guarantees it never covers a form control at ANY scroll position —
  //      a z-index alone would not.
  //
  // Deliberately its own observer rather than a second consumer of the
  // scroll_to_form one above: that one fires at a 0.3 threshold and its
  // one-shot ref feeds a measured funnel metric. Widening its thresholds to
  // serve the chip would put a conversion instrument at risk for a UI detail.
  const [formSeen, setFormSeen] = useState(false)
  const [formInView, setFormInView] = useState(false)
  // biome-ignore lint/correctness/useExhaustiveDependencies: This hook specifies more dependencies than necessary: target.slug. these effects are ANALYTICS instruments on the funnel. Adding a dependency makes an effect RE-RUN, so on a trackEvent effect that is a duplicate conversion event — silent corruption of the numbers ad spend is set from, which nothing here would redden (the Ruby suite parses this source, it does not render it). Deliberate per-site, not deferred: see act:5be85a36 for the per-site plan and the browser check that can falsify it.
  useEffect(() => {
    const el = document.getElementById("signup")
    if (!el) return undefined
    const observer = new IntersectionObserver(([entry]) => {
      setFormInView(entry.isIntersecting)
      if (entry.isIntersecting) setFormSeen(true)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [target.slug])
  const showChip = Boolean(share) && !preview && formSeen && !formInView

  // "Has the reader moved past the first 20px" — ONE boolean with TWO consumers:
  // the nav's shadow-vs-border treatment and the desktop share's reveal. Keep
  // them on the same boolean; they are the same moment.
  //
  // Derived from an IntersectionObserver on the sentinel below rather than from
  // a scroll POSITION: an intersection flips twice per direction, where a
  // position is new state sixty times a second at the page root. The full
  // reasoning and its measured cost live in ScrollProgressBar.jsx (act:aebf07fa).
  //
  // A THIRD observer, deliberately, for the same reason the chip's is its own:
  // the scroll_to_form observer above is a ONE-SHOT at a 0.3 threshold feeding a
  // measured funnel metric, and the chip's watches #signup at the far end of the
  // page. Neither can answer "are we past the top" without being bent out of
  // shape.
  //
  // SSR — this page is server-rendered because its first paint is the ad click's
  // first paint, so the initial value MUST be identical on both sides: a static
  // false, never a lazy read of window.scrollY. Reveal.jsx records what the
  // other shape cost — an initial state branched on `typeof IntersectionObserver`
  // is false in the browser and true under node, and every hydration was
  // discarded. That is also why the no-observer fallback lives INSIDE the effect
  // (effects never run on the server) and fails toward SHOWING the share rather
  // than hiding it forever. What is and is not mechanically guarded is stated
  // once, in Reveal.jsx (act:35de96ff) — and the divergence half, which is what
  // this paragraph is about, is the half that still has no gate.
  const topSentinel = useRef(null)
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      setScrolled(true)
      return undefined
    }
    // The sentinel renders unconditionally below, so this ref is always set by
    // effect time. Kept as a guard rather than an assertion — but note it fails
    // toward `false` where the branch above fails toward `true`: if anyone ever
    // makes the sentinel conditional (preview-gating it is the obvious edit on
    // this page), `scrolled` pins false forever, the nav never shadows and the
    // desktop share NEVER appears, silently. Make the sentinel unconditional or
    // change this direction deliberately.
    const el = topSentinel.current
    if (!el) return undefined
    // Mount-only, so the observer is constructed ONCE. Reveal.jsx records the
    // cost of the other shape on this very page — an unstable ref callback
    // re-attaching Mantine's intersection ref produced ~600 new observers per
    // second, sustained, with a clean console. A perf lane must not ship that.
    //
    // The LAST entry, not the first: one notify task can carry several records
    // for the same target (a fast flick across the line, or a congested main
    // thread), and taking entries[0] would latch the STALE observation until the
    // next crossing — which for the share affordance means it silently vanishes
    // mid-page with nothing logged.
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1]
      setScrolled(!entry.isIntersecting)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Deterministic #signup landing (act:e3aab5d3): the investigation article's CTA
  // deep-links to <funnel>#signup. Chromium retries the fragment after hydration;
  // WebKit has no verified retry, so we own the scroll. Assert at mount, then
  // re-assert ONCE after full load (layout shift can move the target after mount).
  // One-shot guard: re-assert only if the user hasn't taken over (wheel/touch/key —
  // NOT the "scroll" event, which our own programmatic scroll fires) AND the target
  // isn't already in view. readyState handles the load-already-fired (cached) case.
  useEffect(() => {
    if (window.location.hash !== "#signup") return undefined

    let userInteracted = false
    const markInteracted = () => {
      userInteracted = true
    }
    const scrollToSignup = () => {
      const el = document.getElementById("signup")
      if (el) el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" })
    }
    const reassert = () => {
      if (userInteracted) return
      const el = document.getElementById("signup")
      if (!el) return
      const top = el.getBoundingClientRect().top
      if (top < 0 || top >= window.innerHeight) scrollToSignup()
    }

    // Genuine user-intent signals only — a programmatic scrollIntoView also emits
    // "scroll", so keying off that would suppress our own re-assert.
    const passive = { passive: true }
    window.addEventListener("wheel", markInteracted, passive)
    window.addEventListener("touchmove", markInteracted, passive)
    window.addEventListener("keydown", markInteracted)

    scrollToSignup()

    let rafId
    const onLoad = () => {
      rafId = requestAnimationFrame(reassert)
    }
    if (document.readyState === "complete") {
      onLoad()
    } else {
      window.addEventListener("load", onLoad, { once: true })
    }

    return () => {
      window.removeEventListener("wheel", markInteracted, passive)
      window.removeEventListener("touchmove", markInteracted, passive)
      window.removeEventListener("keydown", markInteracted)
      window.removeEventListener("load", onLoad)
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [reduce])

  // The signup form is a movable-but-mandatory section. It renders at its
  // position when "signup" is listed in visible_sections, otherwise at the
  // bottom (backward-compat for campaigns whose order predates the movable
  // form). It always renders — it can never be hidden. id="signup" is kept
  // so the scroll_to_form IntersectionObserver fires wherever it sits.
  //
  // BRAND-NAVY GROUND (operator decision 2026-08-20). This section is the one
  // ground on the page that is not white/wash/band, and that is the whole
  // point: the section ABOVE it (HowItWorks) is bg-white and the wizard card
  // inside it is bg-white, so on a white ground the form had no edge in either
  // direction — it read as the tail of the preceding section rather than as a
  // place the reader arrives at. Navy separates it in both directions at once.
  //
  // THE INVARIANT THIS CREATES, and it is the thing to get wrong: everything
  // VISIBLE in this section must sit inside QualForm's white wizardCard, or be
  // explicitly given on-brand colors here. Today exactly two things render
  // outside that card — this heading block (colored on-brand below) and
  // QualForm's stepAnnouncement (sr-only, so it has no color) — and every
  // funnel default (text-body, text-ink, text-brand, ring-brand) is
  // near-invisible on navy. Adding a line of copy, a link, or a focus ring
  // directly to this section without switching it to the on-brand pair is a
  // silent navy-on-navy defect that renders as an empty gap.
  const renderSignup = () => (
    <section id="signup" className="py-14 bg-brand scroll-mt-24">
      {!signupDisqualified && (
        <div className="max-w-xl mx-auto px-4 text-center mb-2">
          <h2 className="font-serif text-2xl font-bold text-white">
            {lc.signup_heading || "Sign Up With Maginnis Howard"}
          </h2>
          <p className="text-on-brand-faint mt-2">{lc.signup_description || ""}</p>
          {/* A HUMAN AT THE POINT OF HESITATION (act:14fdf580) — distinct from
              the footer colophon, which is identity at the foot of the page.
              This is the alternative offered to someone who has read enough to
              be interested and is not ready to type their details.

              BUILT AND NOT LIVE. `cta_phone` is nil until
              FirmPhoneLink::CTA_PHONE_ENABLED is flipped, which is gated on Ed
              confirming the number is answered by someone who knows these
              campaigns (act:b86e87c4) — a phone number beside the signature
              moment PROMISES that, and publishing the promise before it is true
              is worse than not offering the phone. The prop is ALSO nil if the
              stored number is not safely dialable, so there is one check here
              rather than two.

              Deliberately quiet: faint-on-navy text, no button, no fill. The
              sign-up form directly below is and stays the primary action.
              Colors are the ON-BRAND pair because this section's ground is
              brand navy (see the section comment above) — the earlier
              body-grey + text-brand pair rendered navy-on-navy, and its
              focus ring measured 1:1 against WCAG 2.4.11's 3:1 bar. */}
          {cta_phone && (
            <p className="mt-3 text-sm text-on-brand-faint">
              Prefer to talk to a person first?{" "}
              <a
                href={cta_phone.href}
                className="inline-flex items-center min-h-[44px] whitespace-nowrap font-semibold text-white underline underline-offset-2 hover:text-on-brand-faint focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-brand"
              >
                {cta_phone.display}
              </a>
            </p>
          )}
        </div>
      )}
      <QualForm
        target={target}
        legal_theory={legal_theory}
        case_file_count={case_file_count}
        turnstile_site_key={turnstile_site_key}
        error={error}
        preview={preview}
        marketing_consent_copy={marketing_consent_copy}
        onDisqualifiedChange={setSignupDisqualified}
      />
    </section>
  )

  return (
    <div className="min-h-screen bg-wash text-body">
      {/*
        The server-composed title, rendered verbatim (act:ba8a8d2d). This used to
        be `lc.page_title || `${company} ${region} Claim - ${site}`` — a twin of a
        Ruby formula, kept in step by a parse-the-JSX drift spec, and divergent
        past the 70-char limit because the server clamped and this did not.
        FunnelProps#page_title composes it once now; there is nothing to fall back
        to and no twin to keep in sync. Do NOT reintroduce a `lc.page_title ||`
        fallback here — spec/contracts/funnel_title_drift_spec.rb fails on it.
      */}
      <Head title={document_title} />

      {/* The 20px mark the nav shadow and the desktop share key off (see
          `scrolled` above).

          ABSOLUTE, never in flow. The root div is not positioned, so this
          anchors to the document origin and contributes no height — an in-flow
          20px band would push the sticky nav, every section below it, and the
          offset a #signup deep-link arrival lands at, down by 20px, on the one
          page whose first paint is served from the server.

          It must also keep a real layout box: an element with `display: none`
          reports not-intersecting forever, which would read as "permanently
          scrolled" — nav shadowed and the share revealed at rest. w-px is the
          narrowest box that still has one. */}
      <div
        ref={topSentinel}
        aria-hidden="true"
        className="absolute top-0 left-0 h-5 w-px pointer-events-none"
      />

      {/* Owns its own 60Hz scroll state — see the component (act:aebf07fa). */}
      <ScrollProgressBar />

      {preview && (
        <div
          data-testid="preview-banner"
          // Dark amber-950 text on amber-500 (not white): white-on-amber-500 is
          // ~1.9:1 and fails WCAG 1.4.3 AA; amber-950 on amber-500 clears it
          // comfortably (accessibility-0004).
          className="fixed top-0 left-0 right-0 z-[70] bg-amber-500 text-amber-950 text-center py-2 text-sm font-semibold"
        >
          PREVIEW — {preview.theory_name} ({preview.status}) — not publicly visible. Form submission
          is disabled.
        </div>
      )}

      {/* 1. Nav */}
      {/* py-3 on mobile, py-4 from sm (act:14fdf580). The brand block is two
          lines now instead of one, and this is a STICKY nav on the surface
          where the ad traffic lands — so the added line is paid for out of the
          padding rather than out of the reading area. Net height change on a
          phone is a few pixels, not a row. */}
      <nav
        className={`sticky top-0 z-50 bg-white/85 backdrop-blur transition-shadow animate-fade-up ${scrolled ? "shadow-md" : "border-b border-gray-200"}`}
      >
        <div className="max-w-5xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between gap-3">
          {/* truncate + min-w-0 keep the brand on ONE line and let it take only
              the space left by the (priority) CTA: today's site name
              ("unlawfulcharges.com", 19 chars) shows in full; a longer one
              ellipsizes gracefully instead of wrapping to two lines or pushing
              the CTA off-screen. A smaller mobile size buys more room before
              truncation kicks in.
              NOTE for the next rename: this comment is why a plain line-scoped
              grep for a retired brand can report a FALSE CLEAN — the previous
              name wrapped across these two source lines and no `grep "Hold Them
              To It"` could see it (act:13719e10, CP1). Sweep with a
              whitespace-tolerant pass, not just a literal one. */}
          {/* THE MASTHEAD, brought into parity with the investigation article's
              (act:14fdf580). This nav used to render the bare site name — no
              mark, no tagline, and NO FIRM NAME ANYWHERE — at the exact moment
              a visitor is asked for their name, phone and signature, while the
              article they arrived from carried all three. The trust argument
              made in that action is that THIS, not colour, is the real
              discontinuity between the two surfaces.

              The tagline arrives as the `header_tagline` PROP, resolved
              server-side from the same FirmDetail key the ERB layout renders
              through firm_copy — cross-runtime-shared-vocab option 1. Neither
              runtime contains the string, and {{firm_name}} interpolation stays
              FirmDetail.render's job rather than being re-implemented in JS.

              It is NOT truncated, deliberately: the firm's name sits at the END
              of this line ("Consumer investigations by …"), so truncation would
              cut off the one word on it that has to survive. It wraps instead.
              The site name above it keeps its truncate + min-w-0 so the
              (priority) CTA never gets pushed off-screen. */}
          <span className="flex flex-col min-w-0">
            {/* THE WORDMARK IS THE WAY HOME, the last piece of masthead parity
                with the investigation article (investigation.html.erb's
                a.brand): the mark and the site name are ONE link to the site's
                front door, and the tagline below stays OUTSIDE it so the link's
                accessible name is exactly the site name — the link goes to the
                SITE, not to the firm, which is also why the mark keeps alt="".

                The href arrives as the `home_link_target` PROP, resolved
                server-side by the same helper the ERB layouts call: the homepage
                when it is live, the investigations hub while it is dark
                (act:3804e840). Neither runtime re-derives the rule, so this nav
                can never advertise a URL that still redirects.

                Plain <a>, not an Inertia <Link>: the root is a server-rendered
                ERB page (home#index), so this is a full page load by design —
                the same treatment as the footer's /privacy and /file/request.

                NO aria-current arm, unlike the ERB masthead: that layout is
                shared by every public page type, so its header link can BE the
                current page. This one renders only on /:target_slug, which is
                never the front door.

                py-1 -my-1 buys a 32px tap target out of the nav's EXISTING
                padding — the padded box grows 8px and the negative margin hands
                the same 8px back to the flex column, so the measured nav height
                stated above is unchanged. (A min-h-[44px] like the ERB brand
                link would cost ~20px of sticky nav here, where that masthead is
                one line and this one is two.)

                focus:outline-HIDDEN, never outline-none: the ring is a
                box-shadow and forced-colors mode does not paint box-shadows, so
                the outline-none pairing leaves Windows High Contrast users with
                no focus indicator at all. QualForm.jsx's neutralBtn carries the
                full reasoning. */}
            <a
              href={home_link_target}
              className="flex items-center gap-2 min-w-0 py-1 -my-1 rounded-sm focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
            >
              {/* alt="" — the same treatment as the ERB masthead: this is a
                  brand DEVICE beside the site name, and an alt naming the firm
                  would both duplicate the visible text and trip
                  validate-funnel-copy-exceptions.mjs. width/height are the
                  file's intrinsic 315x375 purely to reserve the aspect box
                  before decode; CSS still sizes it. */}
              <img
                src="/maginnis-monogram.png"
                width={315}
                height={375}
                alt=""
                className="h-6 sm:h-8 w-auto shrink-0"
              />
              {/* text-base on a phone, not text-lg: the mark costs the brand
                  column ~34px, and at 320px that pushed "unlawfulcharges.com"
                  from a tight fit into a hard truncation ("unlawful…"). One
                  size step gives the name back more than the mark took. */}
              <span className="font-serif font-bold text-base sm:text-xl text-ink truncate min-w-0">
                {site_name}
              </span>
            </a>
            {header_tagline && (
              /* Tighter tracking and size on a phone. This line CANNOT fit on
                 one line at 320px at any readable size (41 characters of
                 uppercase), so the only question is two lines or three — and
                 this is a STICKY nav on the surface the ad traffic lands on,
                 where every row costs viewport on every scroll. At 0.68rem/
                 0.12em it wrapped to three; these values give two.

                 THE MEASURED COST OF THIS WHOLE MASTHEAD, at 320px, so nobody
                 has to guess later: the nav goes 69px -> 91px (+22px). The
                 site name truncates — but it ALREADY truncated here before
                 this lane (it had 130px and wanted 184px; it now has 102px and
                 wants 163px), so the mark did not introduce the truncation, it
                 widened an existing 54px shortfall to 61px. Putting the firm's
                 name on the page where a visitor is asked for their signature
                 is worth 22px of sticky nav.

                 THE COLOUR IS text-body/85, NOT /75. At this size (9.92px
                 rendered) the /75 composite is #747c88 on white = 4.18:1, under
                 the 4.5:1 WCAG AA floor for small text — Lighthouse measured 4.22
                 and failed it on the live funnel 2026-08-19. /85 composites to
                 #626a79 = 5.40:1, still visibly lighter than full body text
                 (8.07:1), so the intended de-emphasis survives. Do not restore
                 /75, and do not lighten any small-text alpha past /80 (4.74:1).

                 This note is APPENDED to the comment above rather than given its
                 own braced JSX comment, because everything between the `&& (` and
                 the span below is EXPRESSION position, not JSX children. A braced
                 comment there is a syntax error that fails the entire Vite build
                 — and the build failure surfaces as ~85 unrelated red specs,
                 since every view spec then renders against a missing manifest. */
              <span className="mt-0.5 text-[0.62rem] sm:text-[0.68rem] leading-[1.35] uppercase tracking-[0.08em] sm:tracking-[0.12em] text-body/85">
                {header_tagline}
              </span>
            )}
          </span>
          {/* The DESKTOP share (act:de0f0ed2). Measured: 634px of dead space
              sits between the brand and the CTA at 1280px — so the operator's
              "pinned upper right" costs nothing here, on the one width where it
              is free. Three disciplines keep it from becoming a rival:
              - hidden below sm entirely (12px of slack at 320px; the phone gets
                the lower-right chip instead),
              - muted body-grey TEXT, never the funnel's action-blue and never a
                button, so filled-navy CTA > grey text is a two-rank gap,
              - the rightmost slot stays the CTA, permanently.
              `invisible` (not hidden) until the first scroll keeps it out of the
              first paint and out of any ad screenshot while still reserving its
              width, so nothing reflows when it appears. ml-auto pushes brand and
              this pair apart; the wrapper is `relative` so the clipboard-refusal
              text can float instead of blowing the flex row open. */}
          {share && !preview && (
            <span className="hidden sm:inline-flex items-center shrink-0 relative ml-auto">
              <ShareAction
                share={share}
                label="Share this investigation"
                ariaLabel="Share this investigation"
                className={`inline-flex items-center min-h-[44px] text-sm text-body/80 hover:text-ink underline-offset-2 hover:underline ${scrolled ? "" : "invisible"}`}
                manualClassName="absolute top-full right-0 mt-1 z-50 max-w-[22rem] rounded-md bg-white p-2 text-xs text-gray-600 shadow-md select-all break-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                onShare={() =>
                  trackEvent("share_click", {
                    target_slug: target.slug,
                    theory_slug: target.theory_slug,
                    share_surface: "landing",
                  })
                }
              />
            </span>
          )}
          {/* shrink-0 + whitespace-nowrap stop the CTA wrapping to "See If You /
              Qualify"; tighter mobile padding keeps brand + CTA on one row down
              to ~320px. */}
          <a
            href="#signup"
            className="shrink-0 inline-flex items-center justify-center min-h-[44px] whitespace-nowrap text-sm font-semibold bg-brand text-white px-4 sm:px-5 py-2.5 rounded-md hover:bg-brand-hover transition-all hover:-translate-y-0.5 animate-pulse-cta focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
          >
            See If You Qualify
          </a>
        </div>
      </nav>

      {/* The single <main> landmark: hero + content sections. <nav> (above)
          and <footer> (below) are deliberate SIBLINGS, not descendants, so the
          page exposes exactly one main with banner/contentinfo beside it
          (accessibility-0006). tabindex=-1 so the skip-to-content link can move
          focus here (accessibility-0001). */}
      <main id="main" tabIndex={-1}>
        {/* 2. Hero — the navy band (Establishment direction, act:bf2ebd5f).
          The hero is the ONE section allowed to rely on being first (it is
          locked-top), so a full-bleed brand band is safe; every reorderable
          body block below stays light + self-contained. The old blurred
          bg-brand/10 parallax glow was dropped — invisible on the navy band. */}
        <header className="relative overflow-hidden bg-brand text-white">
          <div className="max-w-3xl mx-auto px-4 pt-20 pb-16 text-center">
            <h1
              className="font-serif text-4xl sm:text-5xl font-bold leading-tight mb-5 text-white animate-fade-up"
              style={{ animationDelay: "0.1s" }}
            >
              {lc.hero_heading || target.company_name}
            </h1>
            <p
              className="text-lg text-on-brand-faint leading-relaxed animate-fade-up"
              style={{ animationDelay: "0.25s" }}
            >
              {lc.hero_subtitle || ""}
            </p>
            <a
              href="#signup"
              className="inline-block mt-8 text-base font-semibold bg-white text-brand px-7 py-3.5 rounded-md hover:bg-band transition-all hover:-translate-y-0.5 animate-fade-up focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-brand"
              style={{ animationDelay: "0.4s" }}
            >
              See If You Qualify
            </a>
          </div>
        </header>

        {(() => {
          const sectionRenderers = {
            // Price comparison — recipe (schema) from theory, data from target
            comparison: () => (
              <SchemaWidget
                schema={legal_theory.comparison_widget_schema}
                data={target.comparison_data}
                disclaimer={comparison_disclaimer}
              />
            ),
            how_it_works: () => <HowItWorks content={lc} />,
            testimonials: () => <Testimonials content={lc} />,
            faq: () => <FAQ content={lc} />,
            signup: renderSignup,
          }
          const sections = lc.visible_sections || [
            "comparison",
            "how_it_works",
            "signup",
            "testimonials",
            "faq",
          ]
          return (
            <>
              {sections.map((key) => {
                const render = sectionRenderers[key]
                return render ? <Fragment key={key}>{render()}</Fragment> : null
              })}
              {/* Backward-compat: campaigns whose visible_sections predate the
                movable form (no "signup" entry) render the form here at the end. */}
              {!sections.includes("signup") && renderSignup()}
            </>
          )
        })()}

        {/* The end-of-page share (act:de0f0ed2), lifted OUT of the footer. Its
          problem was never its depth — it is ~90% down because the page ends
          there — but its WEIGHT: it rendered as one text-xs grey line inside the
          footer's compliance stack, visually identical to the attorney-
          advertising boilerplate beside it. Same position, roughly five times
          the presence.

          NOTE, because a cabinet pass got this wrong and the operator caught it:
          this does NOT reliably sit "just after the form". `visible_sections` is
          campaign-editable, and the live ad campaign orders it
          comparison → how_it_works → SIGNUP → testimonials → faq, so the form
          ends at 55% and ~45% of the page follows. Never reason about this
          page's ordering from a default.

          Last child of <main> on purpose: content stranded between </main> and
          <footer> belongs to no landmark and trips the axe region rule. */}
        {share && (
          <section className="py-8 bg-wash border-t border-gray-200">
            <div className="max-w-xl mx-auto px-4 text-center">
              <p className="text-sm font-semibold text-ink">Know someone this could help?</p>
              <ShareAction
                share={share}
                label="Share this investigation"
                ariaLabel="Share this investigation"
                className="mt-3 inline-flex items-center justify-center min-h-[44px] px-5 rounded-md border border-brand text-brand text-sm font-semibold hover:bg-brand/5"
                onShare={() => {
                  if (!preview)
                    trackEvent("share_click", {
                      target_slug: target.slug,
                      theory_slug: target.theory_slug,
                      share_surface: "landing",
                    })
                }}
              />
            </div>
          </section>
        )}

        {/* The phone chip — see showChip above for the two visibility rules and
          why they are not polish. Inside <main> because a fixed element outside
          every landmark trips the same axe region rule; `fixed` means its
          position in the document costs nothing. z-40 sits under the nav (z-50)
          and the scroll-progress bar (z-[60], now components/ScrollProgressBar.jsx,
          whose header states the whole stack), so neither can ever be covered.
          A WORD, never a bare glyph: the copied state swaps this label to
          "✓ Copied" in place, and an icon-only control has no label node to
          swap — which the cross-runtime share contract checks for. */}
        {showChip && (
          <div className="sm:hidden fixed bottom-4 right-4 z-40">
            <ShareAction
              share={share}
              label="Share"
              ariaLabel="Share this investigation"
              className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-full border border-brand bg-white text-brand text-xs font-semibold shadow-lg"
              manualClassName="absolute bottom-full right-0 mb-2 w-[16rem] rounded-md bg-white p-2 text-xs text-gray-600 shadow-lg select-all break-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              onShare={() =>
                trackEvent("share_click", {
                  target_slug: target.slug,
                  theory_slug: target.theory_slug,
                  share_surface: "landing",
                })
              }
            />
          </div>
        )}
      </main>

      {/* Footer — operating firm + attorney advertising disclaimer (VA Rules 7.1–7.3) */}
      <footer className="border-t border-gray-200 py-10 bg-wash">
        <div className="max-w-3xl mx-auto px-4 text-center space-y-4">
          {/* The share used to live HERE as a single text-xs line (act:46054438).
              It moved out to its own band as the last child of <main>
              (act:de0f0ed2): inside this stack it was indistinguishable from the
              attorney-advertising disclaimer directly below it. Do not
              reintroduce a share line in the footer — one affordance, one place;
              two of them 300px apart is worse than either alone. */}
          {/* The display copy of the mark (447x144, 14 KB), not the 1664x536 /
              100 KB master — see layouts/investigation.html.erb for why both
              exist. h-12 is the largest rendering of this logo anywhere, and the
              display asset is sized at 3x it. */}
          <img
            src="/maginnis-logo-display.png"
            alt={`${firm_name} — ${firm_tagline}`}
            className="h-12 w-auto mx-auto"
            width="447"
            height="144"
            loading="lazy"
            decoding="async"
          />
          <div className="text-xs text-body space-y-2">
            <p>
              <strong>{renderCopy(copy.attorney_advertising_label, footerCopyVars)}</strong>{" "}
              {renderCopy(copy.attorney_advertising_disclaimer, footerCopyVars)}
            </p>
            {lc.eligibility_text && <p>{lc.eligibility_text}</p>}
            <p>
              {/* inline-flex + min-h gives each footer link a >=40px tap target at phone width (act:22ee66c4). */}
              <a
                href="/privacy"
                className="inline-flex items-center min-h-[40px] text-action underline hover:text-action-hover"
              >
                Privacy Policy
              </a>
              {" · "}
              {/* The browser-level advertising opt-out (act:0dc74dbd) — the
                  visible affordance the privacy policy's advertising section
                  points at. Ours, not firm-editable copy: the label names a
                  mechanism the code owns. Plain <a>: server-rendered ERB page. */}
              <a
                href="/ad-preferences"
                className="inline-flex items-center min-h-[40px] text-action underline hover:text-action-hover"
              >
                Advertising Preferences
              </a>
              {" · "}
              <a
                href={firm_website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center min-h-[40px] text-action underline hover:text-action-hover"
              >
                {firm_website_display}
              </a>
              {" · "}
              {/* The returning claimant's way back in (act:22274f4d): the
                  see-your-file front door. Footer on purpose — this page's
                  job is converting an ad click, so the exit ramp stays
                  low-prominence. Plain <a>: /file carries a full-page load. */}
              <a
                href="/file/request"
                className="inline-flex items-center min-h-[40px] text-action underline hover:text-action-hover"
              >
                {renderCopy(copy.case_file_return_prompt, footerCopyVars)}
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}
